/* VilkaMark — fork-A icon mark (logo without the "Вилк" wordmark).
 *
 * Shared 200x200 viewBox geometry; styles for animations live in styles-mark.css.
 *
 * Animations (prop `anim`):
 *   'none'      static
 *   'assemble'  parts fade/slide in once (~700ms)
 *   'breathe'   gentle scale loop
 *   'pulse'     yellow glow ring loop (used for "calculating")
 *   'progress'  yellow fill rises inside A
 *   'sway'      stem-handle nudges like a pendulum
 *   'success'   one-shot pulse + glow
 */

import React, { useMemo } from 'react';

export const MARK_PALETTE = {
  yellow: '#FFDD2D',
  yellowLight: '#FFE57A',
  black: '#1F2226',
  white: '#FFFFFF',
  bg: '#F6F7F8',
};

// Glyph geometry within 200×200 viewBox
export const MARK_GEOM = (() => {
  const apexX = 100, apexY = 78, baseY = 166, stemTopY = 36;
  const legLeftX = 48, legRightX = 152;
  const dy = baseY - apexY;
  const sw = 22;
  const legLen = Math.sqrt((legRightX-apexX)**2 + dy**2);
  const cosTheta = dy / legLen;
  const halfHoriz = (sw/2) / cosTheta;
  const stemW = 2 * halfHoriz;
  const crossY = 132;
  const swH = sw - 2;
  const tineW = sw - 2;
  const leftCenterAt  = (y) => apexX + (legLeftX  - apexX) * (y - apexY) / dy;
  const rightCenterAt = (y) => apexX + (legRightX - apexX) * (y - apexY) / dy;
  const cTop = crossY - swH/2, cBot = crossY + swH/2;
  return {
    apexX, apexY, baseY, stemTopY, legLeftX, legRightX, dy, sw, halfHoriz, stemW,
    crossY, swH, tineW,
    cTop, cBot,
    cTopL: leftCenterAt(cTop) + halfHoriz,
    cTopR: rightCenterAt(cTop) - halfHoriz,
    cBotL: leftCenterAt(cBot) + halfHoriz,
    cBotR: rightCenterAt(cBot) - halfHoriz,
    leftCenterAt, rightCenterAt,
  };
})();

let _markUid = 0;

function ForkAGlyph({ color, stemColor }) {
  const g = MARK_GEOM;
  const sc = stemColor || color;

  const stemLeftX  = g.apexX - g.stemW / 2;
  const stemRightX = g.apexX + g.stemW / 2;

  const leftLegPoints = [
    [stemLeftX,                g.apexY],
    [stemRightX,               g.apexY],
    [g.legLeftX + g.halfHoriz, g.baseY],
    [g.legLeftX - g.halfHoriz, g.baseY],
  ].map(p => p.join(',')).join(' ');

  const rightLegPoints = [
    [stemLeftX,                 g.apexY],
    [stemRightX,                g.apexY],
    [g.legRightX + g.halfHoriz, g.baseY],
    [g.legRightX - g.halfHoriz, g.baseY],
  ].map(p => p.join(',')).join(' ');

  const ov = 3;
  const cTopL = g.leftCenterAt(g.cTop)  + g.halfHoriz - ov;
  const cTopR = g.rightCenterAt(g.cTop) - g.halfHoriz + ov;
  const cBotL = g.leftCenterAt(g.cBot)  + g.halfHoriz - ov;
  const cBotR = g.rightCenterAt(g.cBot) - g.halfHoriz + ov;

  const stemOver = 3;

  return (
    <React.Fragment>
      <rect className="mark-part mark-stem"
        x={stemLeftX} y={g.stemTopY}
        width={g.stemW} height={g.apexY - g.stemTopY + stemOver}
        fill={sc} />
      <polygon className="mark-part mark-leg-left"
        points={leftLegPoints} fill={color} />
      <polygon className="mark-part mark-leg-right"
        points={rightLegPoints} fill={color} />
      <polygon className="mark-part mark-cross"
        points={`${cTopL},${g.cTop} ${cTopR},${g.cTop} ${cBotR},${g.cBot} ${cBotL},${g.cBot}`}
        fill={color} />
      <rect className="mark-part mark-tine"
        x={g.apexX - g.tineW/2} y={g.crossY}
        width={g.tineW} height={g.baseY - g.crossY}
        fill={color} />
    </React.Fragment>
  );
}

export function VilkaMark({
  variant = 'black-square',
  size = 64,
  anim = 'none',
  radius,
  glow = false,
  trigger,
  progress,
  className = '',
  style = {},
  title,
}) {
  const uid = useMemo(() => 'm' + (++_markUid), []);
  const P = MARK_PALETTE;

  let bg, fg, stemFg;
  switch (variant) {
    case 'yellow-square':       bg = P.yellow; fg = P.black; break;
    case 'black-square':        bg = P.black;  fg = P.white; break;
    case 'black-square-yellow': bg = P.black;  fg = P.white; stemFg = P.yellow; break;
    case 'yellow-circle':       bg = P.yellow; fg = P.black; break;
    case 'black-circle':        bg = P.black;  fg = P.white; break;
    case 'soft-square':         bg = P.bg;     fg = P.black; stemFg = P.yellow; break;
    case 'transparent':         bg = 'transparent'; fg = P.black; break;
    case 'outline':             bg = 'transparent'; fg = P.black; break;
    case 'inverted-yellow':     bg = P.yellow; fg = P.black; break;
    default:                    bg = P.black;  fg = P.white;
  }

  const r = radius != null ? radius
          : (variant.includes('circle') ? size / 2 : Math.max(8, size * 0.22));

  const animClass = anim && anim !== 'none' ? `mark-anim-${anim}` : '';
  const key = (anim === 'assemble' || anim === 'success') ? `${anim}-${trigger ?? 0}` : undefined;

  return (
    <div
      className={`vilka-mark ${animClass} ${className}`}
      style={{
        width: size, height: size,
        borderRadius: r,
        background: bg,
        position: 'relative',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        ...style,
      }}
      title={title}
      role={title ? 'img' : undefined}
      aria-label={title}
    >
      {glow && <div className="mark-glow" style={{ borderRadius: r }}></div>}
      <svg
        key={key}
        width="100%" height="100%" viewBox="0 0 200 200"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          {anim === 'progress' && (
            <clipPath id={uid + '-pclip'}>
              {progress != null ? (
                <rect
                  className="mark-progress-static"
                  x="0"
                  y={MARK_GEOM.baseY - (MARK_GEOM.baseY - MARK_GEOM.stemTopY) * Math.max(0, Math.min(100, progress)) / 100}
                  width="200"
                  height="220"
                />
              ) : (
                <rect className="mark-progress-rect" x="0" y={MARK_GEOM.baseY} width="200" height="200" />
              )}
            </clipPath>
          )}
        </defs>

        <g className="mark-glyph">
          <ForkAGlyph color={fg} stemColor={stemFg || fg} />
        </g>

        {anim === 'progress' && (
          <g clipPath={`url(#${uid}-pclip)`}>
            <ForkAGlyph color={fg} stemColor={stemFg || fg} />
          </g>
        )}
      </svg>
    </div>
  );
}
